import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  Plus, TrendingUp, TrendingDown, DollarSign, Film,
  Loader2, Trash2, ChevronLeft, Wallet, CalendarRange, Calendar,
  FileText, Paperclip, History, X as XIcon, ChevronDown, ChevronUp, BarChart3,
  CheckCircle2, RotateCcw, Briefcase, Users, Crown,
  AlertTriangle, Phone, Mail, MessageCircle, ListOrdered, ReceiptText,
  Copy, Pencil, Layers, GitBranch,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { useUndo, UndoRedoFab } from "./UndoStack";
import { FullFrameLogo, ffGradient, ffGradientText } from "./FullFrameLogo";
import { ProductionCreatedPopup } from "./ProductionCreatedPopup";
import { PaymentReminders } from "./PaymentReminders";
import { SuppliersModal } from "./SuppliersModal";
import { ClientAdminSidebar } from "../../components/ClientAdminSidebar";
import {
  listProductions, createProduction, deleteProduction, resolveCompanyIdBySlug,
  duplicateProduction, updateProduction,
  getSummaryForMonth, getSummaryForYear, getCompanyBySlug,
  uploadQuoteFile, uploadAdditionalExpense,
  markProductionComplete, reactivateProduction,
  getPartnerEarnings, getPartnerDetails,
} from "./api";

const ils = (n) => new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(Number(n) || 0);

// ---- contact helpers — used by the "missing client payment" popup so the
// SA can call / WhatsApp / mail the debtor straight from the card.
const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const telHref = (phone) => {
  const d = onlyDigits(phone);
  return d ? `tel:${d}` : null;
};
const waHref = (phone) => {
  const d = onlyDigits(phone);
  if (!d) return null;
  // Normalise IL local 05X… → 9725X… so wa.me works.
  const intl = d.startsWith("0") ? `972${d.slice(1)}` : d;
  return `https://wa.me/${intl}`;
};
const mailHref = (email) => (email ? `mailto:${email}` : null);

// Owner spec 2026-06-15k — pretty Hebrew formatters for completed-date
// badges and cross-month notes on production cards.
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return ""; }
};
const fmtMonth = (iso) => {
  if (!iso) return "";
  const ym = iso.slice(0, 7);
  const [y, m] = ym.split("-");
  return `${HE_MONTHS[parseInt(m, 10) - 1] || ""} ${y}`;
};

// Build YYYY-MM for the current month in the user's local time.
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const currentYear = () => String(new Date().getFullYear());

// Hebrew month name lookup for friendly labels in the dropdown.
const HE_MONTHS = ["ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"];
const formatMonthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return `${HE_MONTHS[parseInt(m, 10) - 1]} ${y}`;
};

export default function FullFrameDashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { pushUndo } = useUndo();

  const [companyId, setCompanyId] = useState(null);
  const [companyDoc, setCompanyDoc] = useState(null);  // full doc — used by ClientAdminSidebar
  // Period mode: "month" → see one specific YYYY-MM, or "year" → see a full YYYY.
  // User asked for a quick toggle between the two so they can scan monthly
  // bookkeeping AND inspect the whole year totals (income / expenses / net).
  const [periodMode, setPeriodMode] = useState("month");
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [summary, setSummary] = useState({
    income: 0, expenses: 0, profit: 0, productions_count: 0,
    income_paid: 0, unpaid_income: 0,
    production_expenses: 0, company_expenses: 0,
    tax_estimated: 0, profit_after_tax: 0,
  });
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addMultiOpen, setAddMultiOpen] = useState(false);
  const [editingProduction, setEditingProduction] = useState(null); // owner spec 2026-07-07c
  const [createdName, setCreatedName] = useState(null);  // triggers success popup
  // Filter: null=all, "single"=non-multi, "multi"=multi+sub
  const [prodFilter, setProdFilter] = useState(null);
  const [inlineCreateOpen, setInlineCreateOpen] = useState(false);
  // Collapsible "נתוני הכנסות" card — closed by default per user request.
  const [revenueOpen, setRevenueOpen] = useState(false);
  // Suppliers management modal — opened from the "ספקים" button.
  const [suppliersOpen, setSuppliersOpen] = useState(false);
  // Partner earnings (Daniel & Nir). Aggregates per period.
  const [partners, setPartners] = useState([]);

  const reload = async (cid) => {
    try {
      const id = cid ?? companyId;
      const periodArg = periodMode === "year" ? { year } : { month };
      const [s, p, pe] = await Promise.all([
        periodMode === "year"
          ? getSummaryForYear(year, id)
          : getSummaryForMonth(month, id),
        listProductions(id),
        getPartnerEarnings(periodArg, id).catch(() => ({ partners: [] })),
      ]);
      setSummary(s);
      setProductions(p);
      setPartners(pe.partners || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בטעינת הנתונים");
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const cd = await getCompanyBySlug(slug);
        setCompanyDoc(cd);
        const cid = cd?.id || (await resolveCompanyIdBySlug(slug));
        setCompanyId(cid);
        await reload(cid);
      } catch (e) {
        toast.error(e.response?.data?.detail || "שגיאה בטעינת החברה");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Re-fetch summary when period (mode / month / year) changes.
  useEffect(() => {
    if (!companyId) return;
    reload(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodMode, month, year]);

  const onCreated = (prod) => {
    setProductions((arr) => [prod, ...arr]);
    setAddOpen(false);
    setCreatedName(prod.name);  // fires the centred confetti popup
    reload();
  };

  const onMultiCreated = (parent, children) => {
    setProductions((arr) => [parent, ...children, ...arr]);
    setAddMultiOpen(false);
    setCreatedName(parent.name);
    reload();
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`למחוק את ההפקה "${name}"?`)) return;
    // Snapshot the full production so we can restore it via the undo button.
    const snapshot = productions.find((p) => p.id === id);
    try {
      await deleteProduction(id, companyId);
      setProductions((arr) => arr.filter((p) => p.id !== id));
      toast.success("נמחק");
      reload();
      if (snapshot) {
        pushUndo({
          label: `הפקה "${name}"`,
          restore: async () => {
            // Re-create from scratch — the new production gets a fresh id but
            // the previously-attached sub-rows (expenses / payments etc.) are
            // not restored (would require a more elaborate snapshot). For the
            // user's "I deleted the wrong card" case this is enough.
            const recreated = await createProduction({
              name: snapshot.name,
              client_name: snapshot.client_name || "",
              client_phone: snapshot.client_phone || "",
              client_email: snapshot.client_email || "",
              client_whatsapp: snapshot.client_whatsapp || "",
              income: snapshot.income || 0,
              notes: snapshot.notes || "",
            }, companyId);
            setProductions((arr) => [recreated, ...arr]);
            reload();
          },
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה במחיקה");
    }
  };

  // Mark/unmark a production as completed. Optimistically update local
  // state so the card moves to the right section immediately; refetch
  // the summary so the totals stay in sync.
  const onComplete = async (id, name) => {
    if (!window.confirm(`לסמן את ההפקה "${name}" כבוצעה בהצלחה?\n\nהיא תועבר לחלק "הפקות שבוצעו בהצלחה" וניתן יהיה להחזיר אותה לכאן בכל עת.`)) return;
    try {
      await markProductionComplete(id, companyId);
      setProductions((arr) => arr.map((p) => p.id === id ? { ...p, is_completed: true, completed_at: new Date().toISOString() } : p));
      toast.success("ההפקה הועברה ל'בוצעו בהצלחה'");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    }
  };

  const onReactivate = async (id, name) => {
    try {
      await reactivateProduction(id, companyId);
      setProductions((arr) => arr.map((p) => p.id === id ? { ...p, is_completed: false, completed_at: null } : p));
      toast.success(`"${name}" הוחזרה להפקות הפעילות`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    }
  };

  // Owner spec 2026-07-07c — clone a production. Backend deep-copies
  // everything and gives us a new doc back so we can splice it in
  // without a full reload.
  const onDuplicate = async (id, name) => {
    if (!window.confirm(`לשכפל את "${name}"?`)) return;
    try {
      const clone = await duplicateProduction(id, companyId);
      setProductions((arr) => [clone, ...arr]);
      toast.success(`"${name}" שוכפלה בהצלחה`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שכפול נכשל");
    }
  };

  const onEditSave = async (patch) => {
    if (!editingProduction) return;
    try {
      await updateProduction(editingProduction.id, patch, companyId);
      setProductions((arr) => arr.map((p) => p.id === editingProduction.id ? { ...p, ...patch } : p));
      toast.success("הכרטיס עודכן");
      setEditingProduction(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "עדכון נכשל");
    }
  };

  // Split productions by lifecycle status. Completed items are hidden
  // under a collapsible "הפקות שבוצעו בהצלחה" section below the active
  // ones — opens with a chevron click.
  const activeProductions = productions.filter((p) => !p.is_completed);
  const completedProductions = productions.filter((p) => p.is_completed);
  // Filtered view of active productions (single | multi | all)
  // Sub-productions (parent_production_id set) are NEVER shown in the main list —
  // they live inside the parent multi-production page.
  const filteredActiveProds = prodFilter === "single"
    ? activeProductions.filter((p) => !p.is_multi_production && !p.parent_production_id)
    : prodFilter === "multi"
    ? activeProductions.filter((p) => p.is_multi_production)
    : activeProductions.filter((p) => !p.parent_production_id);
  const [completedOpen, setCompletedOpen] = useState(false);

  return (
    <div className="min-h-screen bg-stone-800 text-stone-100" dir="rtl">
      <Toaster position="top-center" richColors dir="rtl" theme="dark" />

      {/* Solid uniform grey backdrop — no decorative haloes per user request. */}

      {/* Header */}
      {/* Header — NOT sticky: scrolls away with content so the user gets full
          viewport for tables once they scroll down. */}
      <header className="relative z-10 border-b border-white/10 bg-stone-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <FullFrameLogo size="md" />
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-4 space-y-4">
        {/* Compact hero — title only, no oversized intro paragraph. */}
        <div className="flex items-center justify-between gap-3" data-testid="ff-hero">
          <h1 className="font-heading text-xl sm:text-2xl font-extrabold leading-tight">
            ניהול הפקות חכם
          </h1>
        </div>

        {/* Period selector — compact row:
            [חודשי | שנתי] [picker]   ··   [הוצאות כלליות] [ספקים]
            All on one line, the month/year toggle to the LEFT of the
            picker (was above), and the two pink admin buttons reduced
            to small icons. */}
        <section className="flex flex-wrap items-center gap-2" data-testid="ff-period-bar">
          {/* Mode toggle pill — sits to the LEFT of the picker. */}
          <div className="inline-flex rounded-lg border border-white/10 bg-stone-700 p-0.5" data-testid="ff-period-mode">
            <button
              type="button"
              onClick={() => setPeriodMode("month")}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold transition ${periodMode === "month" ? `${ffGradient} text-white shadow` : "text-stone-300 hover:text-white"}`}
              data-testid="ff-period-mode-month"
            >
              <Calendar className="h-3 w-3" /> חודשי
            </button>
            <button
              type="button"
              onClick={() => setPeriodMode("year")}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold transition ${periodMode === "year" ? `${ffGradient} text-white shadow` : "text-stone-300 hover:text-white"}`}
              data-testid="ff-period-mode-year"
            >
              <CalendarRange className="h-3 w-3" /> שנתי
            </button>
          </div>

          {/* The active picker — month or year. */}
          {periodMode === "month" ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/10 bg-stone-700">
              <input
                type="month"
                value={month}
                dir="ltr"
                onChange={(e) => setMonth(e.target.value || currentMonth())}
                className="bg-transparent text-stone-100 focus:outline-none text-[12px] tabular-nums [color-scheme:dark]"
                data-testid="ff-month-picker"
              />
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/10 bg-stone-700">
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="bg-transparent text-stone-100 focus:outline-none text-[12px] tabular-nums [color-scheme:dark]"
                data-testid="ff-year-picker"
              >
                {(() => {
                  const cur = parseInt(currentYear(), 10);
                  return Array.from({ length: 6 }, (_, i) => cur - i).map((y) => (
                    <option key={y} value={y} className="bg-stone-800">{y}</option>
                  ));
                })()}
              </select>
            </div>
          )}

          {/* Compact pink admin buttons — side-by-side, smaller. */}
          <div className="flex items-center gap-1.5 ms-auto">
            <button
              type="button"
              onClick={() => navigate(`/${slug}/expenses`)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-pink-500/40 bg-pink-500/10 hover:bg-pink-500/20 text-pink-200 text-[11px] font-bold"
              data-testid="ff-company-expenses-btn"
              title="הוצאות עסק כלליות"
            >
              <Wallet className="h-3.5 w-3.5" /> הוצאות עסק כלליות
            </button>
            <button
              type="button"
              onClick={() => setSuppliersOpen(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-pink-500/40 bg-pink-500/10 hover:bg-pink-500/20 text-pink-200 text-[11px] font-bold"
              data-testid="ff-suppliers-btn"
              title="ספקים"
            >
              <Briefcase className="h-3.5 w-3.5" /> ספקים
            </button>
            {/* BackupQuotaTile was here — moved to the admin sidebar tools,
                above "גיבויים חכמים". See `ClientAdminSidebar.jsx`. */}
          </div>
        </section>

        {/* "ניהול חשבונות — חודש מאי 2026" heading removed per owner
            request — the period info is already shown on the cube headers,
            and the standalone H2 was dead screen real-estate. */}
        <section className="space-y-3" data-testid="ff-bookkeeping-block">

          {/* Collapsible "נתוני הכנסות" — single header card that
              expands to reveal the 3 revenue cubes. Closed by default. */}
          <button
            type="button"
            onClick={() => setRevenueOpen((v) => !v)}
            aria-expanded={revenueOpen}
            className={`w-full flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-stone-700 hover:bg-stone-700/80 transition px-4 sm:px-5 py-4 ${revenueOpen ? "rounded-b-none" : ""}`}
            data-testid="ff-revenue-toggle"
          >
            <div className="flex items-center gap-3 min-w-0">
              <BarChart3 className="h-5 w-5 text-pink-400 shrink-0" />
              <span className="font-heading text-lg sm:text-xl font-extrabold text-white">נתוני הכנסות</span>
              <span className="text-[10px] sm:text-xs text-stone-400 font-bold whitespace-nowrap">
                {periodMode === "year" ? `· שנת ${year}` : `· ${formatMonthLabel(month)}`}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Owner spec 2026-06-15d: even when collapsed, surface the
                  bottom-line net profit + period inline on the header so
                  the SA can see the number at a glance without opening
                  the section. */}
              <div className="hidden sm:flex flex-col items-end leading-tight" data-testid="ff-revenue-header-net">
                <span className="text-[10px] text-stone-400 font-bold">רווח נטו · כל ההפקות הפעילות</span>
                <span className={`text-base sm:text-lg font-extrabold tabular-nums ${(summary.profit_after_tax || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                  {ils(summary.profit_after_tax)}
                </span>
              </div>
              <ChevronDown className={`h-6 w-6 text-pink-400 transition-transform duration-300 ${revenueOpen ? "rotate-180" : ""}`} />
            </div>
          </button>

          {revenueOpen && (
          <div className="rounded-2xl rounded-t-none border border-t-0 border-white/10 bg-stone-700 p-2 sm:p-3" data-testid="ff-revenue-content">
            <div className="grid grid-cols-1 gap-2" data-testid="ff-summary">
              {/* Owner spec 2026-06-15b: dashboard income data is now a
                  SINGLE green card showing the company-wide NET profit
                  for the period (after 25% tax + 18% VAT). The red
                  expenses cube was removed because the same breakdown
                  is already visible inside each production card. */}
              <SummaryCube
                label="סה״כ רווח נטו של כל ההפקות הפעילות"
                periodTag={null}
                value={ils(summary.profit_after_tax)}
                icon={DollarSign}
                tone="positive"
                highlight
                detailRows={[
                  { label: "תקציב כל ההפקות", value: ils(summary.per_prod_budgets ?? summary.income) },
                  { label: "הוצאות כל ההפקות", value: ils(summary.per_prod_expenses ?? summary.production_expenses) },
                  { label: "תקציב − הוצאות", value: ils(summary.per_prod_nbt ?? ((summary.income || 0) - (summary.expenses || 0))) },
                  { label: "מע״מ משוער 18% (קודם)", value: ils(summary.vat_to_pay) },
                  { label: "מס משוער 25% (על היתרה)", value: ils(summary.tax_estimated) },
                ]}
                testid="ff-summary-profit"
              />
            </div>
          </div>
          )}
        </section>

        {/* Partner Profit Cards — owner spec 2026-06-14.
            Daniel Hassin & Nir Dadon each earn:
              • staff_earnings  → sum of their crew rows across all productions
              • profit_share    → 50% of agency net profit (after tax)
              • total           → staff_earnings + profit_share
            Data refreshes whenever the period (month/year) changes. */}
        {partners.length > 0 && (
          <PartnerProfitCards partners={partners} periodLabel={periodMode === "year" ? `שנת ${year}` : formatMonthLabel(month)} companyId={companyId} />
        )}

        {/* Productions list */}
        <section className="space-y-4" data-testid="ff-productions-section">
          {/* Header row: title RIGHT · filter buttons LEFT (RTL) */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            {/* Two equal filter toggle buttons */}
            <div className="flex rounded-xl border border-white/10 overflow-hidden" data-testid="ff-filter-tabs">
              <button
                onClick={() => { setProdFilter((f) => f === "single" ? null : "single"); setInlineCreateOpen(false); }}
                className={`px-4 py-2 text-sm font-bold transition-colors ${
                  prodFilter === "single"
                    ? "bg-gradient-to-r from-pink-600 via-fuchsia-600 to-violet-600 text-white"
                    : "bg-stone-800/60 text-stone-300 hover:bg-stone-700 hover:text-white"
                }`}
                data-testid="ff-filter-single"
              >
                הפקות בודדות
              </button>
              <button
                onClick={() => { setProdFilter((f) => f === "multi" ? null : "multi"); setInlineCreateOpen(false); }}
                className={`px-4 py-2 text-sm font-bold transition-colors border-r border-white/10 ${
                  prodFilter === "multi"
                    ? "bg-gradient-to-r from-pink-600 via-fuchsia-600 to-violet-600 text-white"
                    : "bg-stone-800/60 text-stone-300 hover:bg-stone-700 hover:text-white"
                }`}
                data-testid="ff-filter-multi"
              >
                הפקות מרובות
              </button>
            </div>
            <div className="text-right">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2 justify-end">
                הפקות
                <Film className="h-6 w-6 text-pink-400" />
              </h2>
              <p className="text-xs text-stone-500 mt-0.5">
                {filteredActiveProds.length} הפקות פעילות
                {completedProductions.length > 0 ? ` · ${completedProductions.length} הושלמו` : ""}
                {prodFilter && <span className="text-fuchsia-400 mr-1"> · {prodFilter === "single" ? "בודדות" : "מרובות"}</span>}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center text-stone-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : activeProductions.length === 0 && completedProductions.length === 0 ? (
            <EmptyState
              slug={slug}
              onAddSingle={() => { setProdFilter("single"); setInlineCreateOpen(true); }}
              onAddMulti={() => { setProdFilter("multi"); setInlineCreateOpen(true); }}
            />
          ) : (
            <>
              {/* ── "הקם הפקה" inline button + form (only when filter active) ─ */}
              {prodFilter && (
                <div className="space-y-3">
                  {!inlineCreateOpen ? (
                    <button
                      onClick={() => setInlineCreateOpen(true)}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-950/30 hover:border-fuchsia-400 transition font-bold text-sm w-full justify-center"
                      data-testid="ff-inline-create-btn"
                    >
                      <Plus className="h-4 w-4" />
                      הקם הפקה {prodFilter === "single" ? "בודדת" : "מרובה"}
                    </button>
                  ) : (
                    <InlineProductionForm
                      type={prodFilter}
                      companyId={companyId}
                      productions={productions}
                      onCreated={(prod, children) => {
                        if (children) { onMultiCreated(prod, children); }
                        else { onCreated(prod); }
                        setInlineCreateOpen(false);
                      }}
                      onClose={() => setInlineCreateOpen(false)}
                      slug={slug}
                    />
                  )}
                </div>
              )}

              {/* Production cards */}
              {filteredActiveProds.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredActiveProds.map((p) => (
                    <ProductionCard
                      key={p.id}
                      production={p}
                      subProductions={productions.filter((sp) => sp.parent_production_id === p.id)}
                      onOpen={() => navigate(`/${slug}/productions/${p.id}`)}
                      onDelete={() => onDelete(p.id, p.name)}
                      onComplete={() => onComplete(p.id, p.name)}
                      onDuplicate={() => onDuplicate(p.id, p.name)}
                      onEdit={() => setEditingProduction(p)}
                      viewMonth={month}
                      viewYear={year}
                      periodMode={periodMode}
                    />
                  ))}
                </div>
              )}
              {filteredActiveProds.length === 0 && prodFilter && (
                <p className="py-8 text-center text-stone-500 text-sm">
                  אין הפקות {prodFilter === "single" ? "בודדות" : "מרובות"} פעילות.
                </p>
              )}
              {filteredActiveProds.length === 0 && !prodFilter && completedProductions.length > 0 && (
                <p className="py-8 text-center text-stone-500 text-sm">
                  אין הפקות פעילות. כל ההפקות נמצאות באזור "בוצעו בהצלחה" למטה.
                </p>
              )}
            </>
          )}

          {/* Successfully completed productions — collapsible. Click the
              chevron in the header to reveal the cards. Each card has a
              "החזר להפקות פעילות" button to restore it. */}
          {!loading && completedProductions.length > 0 && (
            <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-950/30 overflow-hidden" data-testid="ff-completed-section">
              <button
                type="button"
                onClick={() => setCompletedOpen((s) => !s)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-emerald-900/30 transition select-none cursor-pointer text-right"
                data-testid="ff-completed-toggle"
              >
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading text-lg sm:text-xl font-bold text-emerald-100">
                      הפקות שבוצעו בהצלחה
                    </h3>
                    <p className="text-xs text-emerald-300/70 mt-0.5">{completedProductions.length} הפקות</p>
                  </div>
                </div>
                {completedOpen ? <ChevronUp className="h-5 w-5 text-emerald-300" /> : <ChevronDown className="h-5 w-5 text-emerald-300" />}
              </button>
              {completedOpen && (
                <div className="px-5 pb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ff-completed-list">
                  {completedProductions.map((p) => (
                    <ProductionCard
                      key={p.id}
                      production={p}
                      subProductions={productions.filter((sp) => sp.parent_production_id === p.id)}
                      onOpen={() => navigate(`/${slug}/productions/${p.id}`)}
                      onDelete={() => onDelete(p.id, p.name)}
                      onReactivate={() => onReactivate(p.id, p.name)}
                      onDuplicate={() => onDuplicate(p.id, p.name)}
                      onEdit={() => setEditingProduction(p)}
                      viewMonth={month}
                      viewYear={year}
                      periodMode={periodMode}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      <AddProductionDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={onCreated}
        companyId={companyId}
        productions={productions}
      />

      <AddMultiProductionDialog
        open={addMultiOpen}
        onOpenChange={setAddMultiOpen}
        onMultiCreated={onMultiCreated}
        companyId={companyId}
      />

      {/* Owner spec 2026-07-07c — edit-title dialog for existing cards. */}
      <EditProductionDialog
        production={editingProduction}
        onClose={() => setEditingProduction(null)}
        onSave={onEditSave}
      />

      {/* Centred success popup with 360° confetti burst. */}
      <ProductionCreatedPopup name={createdName} onClose={() => setCreatedName(null)} />

      {/* Future-payment reminder system — polls productions list for any
          scheduled payment whose time has arrived and pops the modal. */}
      <PaymentReminders companyId={companyId} productions={productions} />

      {/* Admin-only sidebar (toggles via the floating shield FAB). */}
      <ClientAdminSidebar company={companyDoc} />

      {/* Suppliers management modal. */}
      <SuppliersModal open={suppliersOpen} onOpenChange={setSuppliersOpen} companyId={companyId} />

      {/* Floating undo/redo controls — top-left corner, vertical stack,
          stays out of the way of the FAB & content. */}
      <div className="fixed top-20 left-4 z-[10100]">
        <UndoRedoFab />
      </div>
    </div>
  );
}

// ---------------- Summary Cube — horizontal layout matching user spec ----
// Title bar at top with period tag. Below: 2-column row — big total
// (left) and small details list (right). Stacks vertically so each
// card spans the full row.
function SummaryCube({ label, periodTag, value, detailRows, icon: Icon, tone, highlight, testid }) {
  const toneStyles = {
    positive: {
      surface: "bg-emerald-950 border-emerald-600/60",
      glow: "shadow-[0_0_24px_-12px_rgba(16,185,129,0.5)]",
      icon: "text-emerald-300",
      bigValue: "text-emerald-200",
      labelClass: "text-emerald-300",
      detailLabel: "text-emerald-200/80",
    },
    negative: {
      surface: "bg-rose-950 border-rose-600/60",
      glow: "shadow-[0_0_24px_-12px_rgba(244,63,94,0.5)]",
      icon: "text-rose-300",
      bigValue: "text-rose-200",
      labelClass: "text-rose-200",
      detailLabel: "text-rose-200/80",
    },
    info: {
      surface: "bg-sky-950 border-sky-600/60",
      glow: "shadow-[0_0_24px_-12px_rgba(14,165,233,0.5)]",
      icon: "text-sky-300",
      bigValue: "text-sky-200",
      labelClass: "text-sky-300",
      detailLabel: "text-sky-200/80",
    },
  }[tone] || {
    surface: "bg-stone-700 border-white/10",
    glow: "",
    icon: "text-stone-300",
    bigValue: "text-stone-100",
    labelClass: "text-stone-100",
    detailLabel: "text-stone-300",
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border px-3 py-3 sm:py-4 transition-all ${toneStyles.surface} ${highlight ? toneStyles.glow : ""}`}
      data-testid={testid}
    >
      {/* Owner spec 2026-06-15f — ONE-LINE compact layout. RTL flow:
            [icon] [label · period]  →  [details inline · separated by dots]  →  [big total on the left]
          Removes the huge empty area on the left of the value pill. */}
      <div className="flex items-center justify-between gap-3 sm:gap-4 flex-wrap">
        {/* RIGHT (in RTL) — heading */}
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`h-4 w-4 shrink-0 ${toneStyles.icon}`} />
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className={`text-xs sm:text-sm font-extrabold uppercase tracking-wider truncate ${toneStyles.labelClass}`}>
              {label}
            </span>
            {periodTag ? (
              <span className={`text-[10px] sm:text-xs font-normal opacity-80 ${toneStyles.labelClass}`}>{periodTag}</span>
            ) : null}
          </div>
        </div>

        {/* MIDDLE — inline detail rows. On desktop they form a row of
            chips, separated by subtle dividers. On mobile they wrap. */}
        <div className="flex items-center flex-wrap gap-x-3 sm:gap-x-4 gap-y-1 flex-1 justify-end" data-testid={`${testid}-details`}>
          {(detailRows || []).map((r, i) => {
            const valTone =
              r.tone === "yellow" ? "text-yellow-300" :
              r.tone === "red" ? "text-rose-300" :
              r.tone === "emerald" ? "text-emerald-300" : "text-white";
            return (
              <div key={i} className="flex items-baseline gap-1.5 whitespace-nowrap">
                <span className={`text-[10px] sm:text-[11px] ${toneStyles.detailLabel} font-semibold`}>{r.label}:</span>
                <span className={`text-xs sm:text-sm font-extrabold tabular-nums ${valTone}`}>{r.value}</span>
              </div>
            );
          })}
        </div>

        {/* LEFT (in RTL) — the headline number. */}
        <div
          className={`font-heading font-extrabold tabular-nums leading-none text-2xl sm:text-3xl ${toneStyles.bigValue} ps-3 sm:ps-4 border-s-2 ${toneStyles.surface.includes('emerald') ? 'border-emerald-800/60' : toneStyles.surface.includes('rose') ? 'border-rose-800/60' : toneStyles.surface.includes('sky') ? 'border-sky-800/60' : 'border-white/10'} shrink-0`}
          data-testid={`${testid}-big`}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

// ---------------- Production Card ----------------
function ProductionCard({ production, subProductions = [], onOpen, onDelete, onComplete, onReactivate, onDuplicate, onEdit, viewMonth, viewYear, periodMode }) {
  const expenseRows = production.expenses || [];
  const additionalRows = production.additional_expenses || [];
  const totalExp = expenseRows.reduce(
    (s, r) => s + (Number(r.amount) || 0) + (Number(r.vat_total) || 0), 0,
  ) + additionalRows.reduce(
    (s, r) => s + (Number(r.total_with_vat) || 0), 0,
  );
  const rawBudget = Number(production.client_budget) || 0;
  // Owner spec 2026-06-15k — when the budget is marked "פלוס מע״מ 18%",
  // the actual GROSS income that the client pays is budget × 1.18.
  // The per-card net profit must use the GROSS figure so it matches
  // both the production page summary AND the dashboard total.
  const includesVat = !!production.client_budget_includes_vat;
  const budget = includesVat ? rawBudget * 1.18 : rawBudget;
  // Owner spec 2026-06-15e — SEQUENTIAL net profit calc:
  //   net before tax → −18% VAT → result × 0.75 (−25% tax) = net.
  const netBeforeTax = budget - totalExp;
  const afterVat = netBeforeTax >= 0 ? netBeforeTax * 0.82 : netBeforeTax;
  const netProfit = netBeforeTax >= 0 ? afterVat * 0.75 : netBeforeTax;

  // ----- Owner spec 2026-06-15i: missing-payment & missing-invoice indicators.
  const clientTotalOwed = budget;  // gross — same number the client actually has to pay
  const clientPaidAmount = (production.payments || []).reduce(
    (s, pay) => s + (pay.paid ? Number(pay.amount) || 0 : 0), 0,
  );
  const missingAmount = Math.max(0, clientTotalOwed - clientPaidAmount);
  const hasMissingClientPayment = rawBudget > 0 && missingAmount > 0.5;
  const providersMissingInvoices = expenseRows.flatMap((row) => {
    const missing = (row.payments || []).filter((pay) =>
      (Number(pay.amount) || 0) > 0 && !pay.invoice_url && !pay.invoice_filename
    );
    if (!missing.length) return [];
    return [{
      row_id: row.id,
      provider: row.provider || "—",
      role: row.role || "",
      provider_phone: row.provider_phone || row.provider_whatsapp || "",
      total_missing: missing.reduce((s, p) => s + (Number(p.amount) || 0), 0),
      count: missing.length,
    }];
  });
  const hasMissingInvoices = providersMissingInvoices.length > 0;

  // Owner spec 2026-06-15k — every production card stays on the dashboard
  // regardless of which month is selected. When the production was
  // created in a DIFFERENT month than the one currently being viewed,
  // surface that fact under the net-profit pill so the SA understands
  // the budget belongs to another month's books.
  const createdAt = production.created_at || "";
  const prodMonth = createdAt.slice(0, 7);  // "YYYY-MM"
  const prodYear = createdAt.slice(0, 4);
  const currentYM = periodMode === "year" ? null : viewMonth;
  const currentY = periodMode === "year" ? viewYear : null;
  const showOtherMonth =
    periodMode === "month" ? (prodMonth && currentYM && prodMonth !== currentYM)
    : (prodYear && currentY && prodYear !== String(currentY));
  const completedAt = production.completed_at || "";

  const [missingPayOpen, setMissingPayOpen] = useState(false);
  const [missingInvOpen, setMissingInvOpen] = useState(false);
  // Card is collapsible — default CLOSED per owner spec.
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`group relative rounded-2xl border transition-all shadow-lg shadow-black/20 ${
        production.is_completed
          ? "border-emerald-500/30 bg-emerald-950/40 hover:border-emerald-400/60"
          : "border-pink-500/30 bg-pink-950 hover:border-pink-400/60"
      }`}
      data-testid={`ff-production-card-${production.id}`}
    >
      <div className="p-4 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 cursor-pointer" onClick={onOpen} data-testid={`ff-prod-open-${production.id}`}>
          <h3 className="font-heading text-lg font-bold truncate text-white">{production.name}</h3>
          {/* Multi-production / sub-production badges */}
          {production.is_multi_production && (
            <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold bg-pink-500/20 border border-pink-500/40 text-pink-300 rounded-full px-2 py-0.5">
              <Layers className="h-3 w-3" />
              הפקה מרובה
            </span>
          )}
          {production.parent_production_id && !production.is_multi_production && (
            <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-300 rounded-full px-2 py-0.5">
              <GitBranch className="h-3 w-3" />
              תת הפקה
            </span>
          )}
          {production.client_name && (
            <p className="text-xs text-stone-400 truncate mt-0.5">לקוח: {production.client_name}</p>
          )}
          {/* Owner spec 2026-06-15k — start date is shown on every card,
              end date appears only when the production is completed. */}
          {createdAt && (
            <p
              className={`text-[10px] mt-1 truncate font-bold ${production.is_completed ? "text-emerald-300/80" : "text-pink-300/70"}`}
              data-testid={`ff-prod-dates-${production.id}`}
            >
              <span>התחלה: {fmtDate(createdAt)}</span>
              {production.is_completed && completedAt && (
                <span> · סיום: {fmtDate(completedAt)}</span>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Owner spec 2026-07-08e — edit + duplicate buttons enlarged with
              explicit colored backgrounds so the SA can find them easily. */}
          {onEdit && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="bg-sky-500/25 hover:bg-sky-500 text-sky-200 hover:text-white transition rounded-lg px-2 py-1.5 flex items-center gap-1 font-bold text-[11px] border border-sky-400/40"
              data-testid={`ff-edit-prod-${production.id}`}
              aria-label="ערוך כרטיס"
              title="ערוך כרטיס"
            >
              <Pencil className="h-4 w-4" />
              <span>ערוך</span>
            </button>
          )}
          {onDuplicate && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              className="bg-indigo-500/25 hover:bg-indigo-500 text-indigo-200 hover:text-white transition rounded-lg px-2 py-1.5 flex items-center gap-1 font-bold text-[11px] border border-indigo-400/40"
              data-testid={`ff-duplicate-prod-${production.id}`}
              aria-label="שכפל כרטיס"
              title="שכפל כרטיס"
            >
              <Copy className="h-4 w-4" />
              <span>שכפל</span>
            </button>
          )}
          {/* Action button: complete OR reactivate depending on status. */}
          {!production.is_completed && onComplete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onComplete(); }}
              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/15 transition rounded-lg p-1.5"
              data-testid={`ff-complete-prod-${production.id}`}
              aria-label="הפקה בוצעה בהצלחה"
              title="הפקה בוצעה בהצלחה"
            >
              <CheckCircle2 className="h-5 w-5" />
            </button>
          )}
          {production.is_completed && onReactivate && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onReactivate(); }}
              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/15 transition rounded-lg p-1.5"
              data-testid={`ff-reactivate-prod-${production.id}`}
              aria-label="החזר להפקות פעילות"
              title="החזר להפקות פעילות"
            >
              <RotateCcw className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition rounded-lg p-1.5"
            data-testid={`ff-delete-prod-${production.id}`}
            aria-label="מחק הפקה"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Flashing alert buttons row — visible only when there's something
          to nag the SA about. Side-by-side, fit-content, tucked between
          the header and the net-profit pill. */}
      {(hasMissingClientPayment || hasMissingInvoices) && (
        <div className="px-4 flex flex-wrap items-center gap-1.5 mb-2" data-testid={`ff-prod-alerts-${production.id}`}>
          {hasMissingClientPayment && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setMissingPayOpen(true); }}
              className="inline-flex items-center gap-1 rounded-lg border border-rose-400/70 bg-rose-600/20 hover:bg-rose-600/30 text-rose-100 text-[10.5px] font-extrabold px-2 py-1 animate-pulse shadow-[0_0_12px_-3px_rgba(244,63,94,0.6)]"
              data-testid={`ff-prod-missing-pay-btn-${production.id}`}
              title="חסרים תשלומי לקוח"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              חסרים תשלומי לקוח
            </button>
          )}
          {hasMissingInvoices && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setMissingInvOpen(true); }}
              className="inline-flex items-center gap-1 rounded-lg border border-amber-400/70 bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-[10.5px] font-extrabold px-2 py-1 animate-pulse shadow-[0_0_12px_-3px_rgba(245,158,11,0.6)]"
              data-testid={`ff-prod-missing-inv-btn-${production.id}`}
              title="חסרות חשבוניות מספקים"
            >
              <ReceiptText className="h-3.5 w-3.5" />
              חסרות חשבוניות
            </button>
          )}
        </div>
      )}

      {/* Net profit pill / multi-production summary */}
      <div className="px-4">
        {production.is_multi_production ? (
          /* ── Multi-production: breakdown by sub-production ── */
          subProductions.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-fuchsia-500/30 px-3 py-3 text-center">
              <span className="text-xs text-fuchsia-300/60">עוד אין תת הפקות — פתח להוסיף</span>
            </div>
          ) : (
            <div className="rounded-xl border-2 border-fuchsia-500/40 overflow-hidden bg-fuchsia-950/20">
              {/* Total budget row */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-fuchsia-500/20 bg-fuchsia-950/30">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-fuchsia-300/80">תקציב הפקה</span>
                <span className="font-extrabold text-fuchsia-100 tabular-nums text-sm">
                  {ils(Number(production.client_budget) || 0)}
                </span>
              </div>
              {/* Sub-production profit rows */}
              {subProductions.map((sp) => {
                const spRaw = Number(sp.client_budget) || 0;
                const spBudget = sp.client_budget_includes_vat ? spRaw * 1.18 : spRaw;
                const spExp = (sp.expenses || []).reduce((s, r) => s + (Number(r.amount) || 0) + (Number(r.vat_total) || 0), 0)
                  + (sp.additional_expenses || []).reduce((s, r) => s + (Number(r.total_with_vat) || 0), 0);
                const spNbt = spBudget - spExp;
                const spNet = spNbt >= 0 ? spNbt * 0.82 * 0.75 : spNbt;
                return (
                  <div key={sp.id} className="flex items-center justify-between px-3 py-1.5 border-b border-white/5 last:border-b-0">
                    <span className="text-[11px] text-stone-300 truncate max-w-[55%]">{sp.name}</span>
                    <span className={`text-xs font-bold tabular-nums ${spNet >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {ils(spNet)}
                    </span>
                  </div>
                );
              })}
              {/* Total net profit */}
              {(() => {
                const totalNet = subProductions.reduce((acc, sp) => {
                  const spRaw = Number(sp.client_budget) || 0;
                  const spBudget = sp.client_budget_includes_vat ? spRaw * 1.18 : spRaw;
                  const spExp = (sp.expenses || []).reduce((s, r) => s + (Number(r.amount) || 0) + (Number(r.vat_total) || 0), 0)
                    + (sp.additional_expenses || []).reduce((s, r) => s + (Number(r.total_with_vat) || 0), 0);
                  const spNbt = spBudget - spExp;
                  return acc + (spNbt >= 0 ? spNbt * 0.82 * 0.75 : spNbt);
                }, 0);
                return (
                  <div className={`flex items-center justify-between px-3 py-2 border-t border-fuchsia-500/20 ${totalNet >= 0 ? "bg-emerald-950/60" : "bg-rose-950/60"}`}>
                    <span className="text-[11px] font-extrabold uppercase tracking-widest text-white/80">נטו רווח</span>
                    <span className={`text-xl font-extrabold tabular-nums ${totalNet >= 0 ? "text-emerald-200" : "text-rose-200"}`}
                      data-testid={`ff-prod-net-${production.id}`}>
                      {ils(totalNet)}
                    </span>
                  </div>
                );
              })()}
            </div>
          )
        ) : (
          /* ── Single / sub-production: existing net-profit pill ── */
          <div className={`rounded-xl px-3 py-2.5 flex items-center justify-between gap-2 border-2 ${
            netProfit >= 0
              ? "bg-emerald-950 border-emerald-500/50"
              : "bg-rose-950 border-rose-500/50"
          }`}>
            <span className="text-[11px] uppercase tracking-widest font-extrabold text-white/80">רווח נטו</span>
            <span className={`text-xl font-extrabold tabular-nums ${netProfit >= 0 ? "text-emerald-200" : "text-rose-200"}`} data-testid={`ff-prod-net-${production.id}`}>
              {ils(netProfit)}
            </span>
          </div>
        )}
        {/* Owner spec 2026-06-15k — show "סכום משויך ל-[חודש]" when the
            production was created in a different month than the one being viewed. */}
        {showOtherMonth && createdAt && (
          <p className="mt-1 text-[10px] text-amber-300/90 text-right font-bold" data-testid={`ff-prod-other-month-${production.id}`}>
            סכום משויך ל-{fmtMonth(createdAt)}
          </p>
        )}
      </div>

      {/* Collapsible expand button at the bottom */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="w-full mt-2 px-4 py-2 flex items-center justify-between text-[11px] font-bold text-pink-300 hover:text-pink-200"
        data-testid={`ff-prod-toggle-${production.id}`}
        aria-expanded={open}
      >
        <span>{open ? "פחות פרטים" : "הצג פרטים"}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2 text-xs" data-testid={`ff-prod-details-${production.id}`}>
          <Row label="תקציב פרויקט" value={ils(budget)} tone="info" />
          <Row label="סה״כ הוצאות" value={ils(totalExp)} tone="negative" />
          <Row label="תקציב − הוצאות" value={ils(netBeforeTax)} tone="muted" />
          <button
            type="button"
            onClick={onOpen}
            className="w-full mt-2 inline-flex items-center justify-center gap-1 text-xs font-bold text-pink-400 hover:text-pink-300 underline"
          >
            פתח את ההפקה
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Popups */}
      <MissingClientPaymentDialog
        open={missingPayOpen}
        onClose={() => setMissingPayOpen(false)}
        production={production}
        missingAmount={missingAmount}
        clientTotalOwed={clientTotalOwed}
        clientPaidAmount={clientPaidAmount}
      />
      <MissingInvoicesDialog
        open={missingInvOpen}
        onClose={() => setMissingInvOpen(false)}
        production={production}
        items={providersMissingInvoices}
      />
    </div>
  );
}

// ---- Missing client-payment popup ----
function MissingClientPaymentDialog({ open, onClose, production, missingAmount, clientTotalOwed, clientPaidAmount }) {
  const phone = production?.client_phone || "";
  const email = production?.client_email || "";
  const wa = production?.client_whatsapp || phone;
  const contactName = production?.contact_name || production?.client_name || "הלקוח";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent dir="rtl" className="bg-stone-900 border-rose-500/30 text-stone-100 sm:max-w-md" data-testid={`ff-missing-pay-dialog-${production?.id}`}>
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-rose-400" />
            <span className="text-rose-200">תשלום לקוח חסר</span>
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400">
            {production?.name ? `הפקה: ${production.name}` : ""}
            {production?.client_name ? ` · ${production.client_name}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 pt-1">
          <div className="rounded-xl border border-rose-500/40 bg-rose-950/40 p-3">
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] uppercase tracking-widest text-rose-200 font-bold">סכום חסר</span>
              <span className="text-2xl font-extrabold tabular-nums text-rose-100" data-testid={`ff-missing-pay-amount-${production?.id}`}>
                {ils(missingAmount)}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
              <div className="flex items-center justify-between rounded-md bg-black/30 px-2 py-1">
                <span className="text-stone-400">סה״כ לחיוב</span>
                <span className="font-bold tabular-nums">{ils(clientTotalOwed)}</span>
              </div>
              <div className="flex items-center justify-between rounded-md bg-black/30 px-2 py-1">
                <span className="text-stone-400">שולם עד כה</span>
                <span className="font-bold tabular-nums">{ils(clientPaidAmount)}</span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-stone-800/60 p-3 space-y-1.5">
            <div className="text-[11px] uppercase tracking-widest text-stone-400 font-bold mb-1">פרטי לקוח</div>
            <div className="text-sm font-bold text-white truncate">{contactName}</div>
            {phone && <div className="text-xs text-stone-300 ltr:text-left" dir="ltr">{phone}</div>}
            {email && <div className="text-xs text-stone-300" dir="ltr">{email}</div>}
            {!phone && !email && (
              <div className="text-xs text-stone-500">לא הוזנו פרטי לקוח להפקה זו.</div>
            )}
          </div>

          {/* Quick contact buttons */}
          <div className="grid grid-cols-3 gap-2 pt-1">
            <a
              href={telHref(phone) || undefined}
              onClick={(e) => { if (!phone) e.preventDefault(); }}
              className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-bold transition ${phone ? "border-sky-500/40 bg-sky-500/15 hover:bg-sky-500/25 text-sky-100" : "border-white/10 bg-stone-800/40 text-stone-500 cursor-not-allowed"}`}
              data-testid={`ff-missing-pay-call-${production?.id}`}
            >
              <Phone className="h-4 w-4" />
              חיוג
            </a>
            <a
              href={waHref(wa) || undefined}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => { if (!wa) e.preventDefault(); }}
              className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-bold transition ${wa ? "border-emerald-500/40 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-100" : "border-white/10 bg-stone-800/40 text-stone-500 cursor-not-allowed"}`}
              data-testid={`ff-missing-pay-wa-${production?.id}`}
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
            <a
              href={mailHref(email) || undefined}
              onClick={(e) => { if (!email) e.preventDefault(); }}
              className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-bold transition ${email ? "border-fuchsia-500/40 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100" : "border-white/10 bg-stone-800/40 text-stone-500 cursor-not-allowed"}`}
              data-testid={`ff-missing-pay-mail-${production?.id}`}
            >
              <Mail className="h-4 w-4" />
              מייל
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Missing provider invoices popup ----
function MissingInvoicesDialog({ open, onClose, production, items }) {
  const total = items.reduce((s, it) => s + (Number(it.total_missing) || 0), 0);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent dir="rtl" className="bg-stone-900 border-amber-500/30 text-stone-100 sm:max-w-md max-h-[80vh] overflow-y-auto" data-testid={`ff-missing-inv-dialog-${production?.id}`}>
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <ReceiptText className="h-5 w-5 text-amber-400" />
            <span className="text-amber-100">חשבוניות ספקים חסרות</span>
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400">
            {production?.name ? `הפקה: ${production.name}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 pt-1">
          {items.length === 0 ? (
            <p className="py-6 text-center text-stone-500 text-sm">כל הספקים מסרו חשבוניות.</p>
          ) : (
            <>
              <ul className="space-y-1.5">
                {items.map((it, idx) => (
                  <li
                    key={`${it.row_id}-${idx}`}
                    className="rounded-lg border border-amber-500/30 bg-amber-950/30 px-3 py-2 flex items-center justify-between gap-2"
                    data-testid={`ff-missing-inv-row-${production?.id}-${idx}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-amber-100 truncate">{it.provider}</div>
                      {it.role && <div className="text-[11px] text-amber-300/70 truncate">{it.role}</div>}
                      <div className="text-[10px] text-stone-400">{it.count} תשלומים ללא חשבונית</div>
                    </div>
                    <div className="text-left shrink-0">
                      <div className="text-base font-extrabold tabular-nums text-amber-200">{ils(it.total_missing)}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between pt-2 border-t border-white/10 mt-2">
                <span className="text-xs text-stone-400 font-semibold">{items.length} ספקים</span>
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">סה״כ ללא חשבונית</span>
                  <span className="text-lg font-extrabold tabular-nums text-amber-200" data-testid={`ff-missing-inv-total-${production?.id}`}>
                    {ils(total)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Tiny row helper used by ProductionCard's expanded view.
function Row({ label, value, tone }) {
  const c = {
    info:     { bg: "bg-sky-950/50 border-sky-700/40",         label: "text-sky-200",     value: "text-sky-100" },
    negative: { bg: "bg-rose-950/50 border-rose-700/40",       label: "text-rose-200",    value: "text-rose-100" },
    muted:    { bg: "bg-stone-800/60 border-white/10",         label: "text-stone-300",   value: "text-stone-100" },
  }[tone] || { bg: "bg-stone-800/60 border-white/10", label: "text-stone-300", value: "text-stone-100" };
  return (
    <div className={`rounded-lg border px-2.5 py-1.5 flex items-center justify-between ${c.bg}`}>
      <span className={`text-[11px] font-bold ${c.label}`}>{label}</span>
      <span className={`text-sm font-extrabold tabular-nums ${c.value}`}>{value}</span>
    </div>
  );
}

// ---------------- Empty State ----------------
function EmptyState({ slug, onAddSingle, onAddMulti }) {
  return (
    <div className="text-center py-16 rounded-2xl border border-dashed border-white/10 bg-white/[0.02]" data-testid="ff-empty">
      <div className={`mx-auto h-16 w-16 rounded-2xl ${ffGradient} flex items-center justify-center shadow-lg shadow-pink-500/20`}>
        <Film className="h-8 w-8 text-white" />
      </div>
      <h3 className="mt-4 font-heading text-xl font-bold">עוד אין הפקות</h3>
      <p className="text-sm text-stone-500 mt-1">צור את ההפקה הראשונה שלך כדי להתחיל</p>
      <div className="flex items-center justify-center gap-3 mt-5">
        <Button onClick={onAddSingle} variant="outline" className="border-white/20 bg-stone-800/60 text-stone-200 hover:bg-stone-700 font-bold" data-testid="ff-empty-add-btn">
          <Film className="h-4 w-4 ml-1.5" />
          הפקה בודדת
        </Button>
        <Button onClick={onAddMulti} className={`${ffGradient} text-white font-bold`} data-testid="ff-empty-add-multi-btn">
          <Layers className="h-4 w-4 ml-1.5" />
          הפקה מרובה
        </Button>
      </div>
    </div>
  );
}

// ---------------- Edit Dialog (owner spec 2026-07-07c) ----------------
function EditProductionDialog({ production, onClose, onSave }) {
  const open = !!production;
  const [form, setForm] = useState({ name: "", client_name: "", contact_name: "", contact_phone: "", location: "" });
  useEffect(() => {
    if (!production) return;
    setForm({
      name: production.name || "",
      client_name: production.client_name || "",
      contact_name: production.contact_name || "",
      contact_phone: production.contact_phone || "",
      location: production.location || "",
    });
  }, [production]);
  const submit = (e) => {
    e?.preventDefault();
    if (!form.name.trim()) { toast.error("שם ההפקה חובה"); return; }
    onSave(form);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-md" data-testid="ff-edit-prod-dialog">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <Pencil className="h-5 w-5 text-sky-400" />
            עריכת כרטיס הפקה
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-2 pt-1">
          <label className="block text-xs font-bold text-stone-300">שם ההפקה</label>
          <Input value={form.name}         onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}         className="bg-stone-800 border-white/10 text-stone-100" data-testid="ff-edit-prod-name" />
          <label className="block text-xs font-bold text-stone-300">שם החברה / לקוח</label>
          <Input value={form.client_name}  onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}  className="bg-stone-800 border-white/10 text-stone-100" data-testid="ff-edit-prod-client" />
          <label className="block text-xs font-bold text-stone-300">איש קשר בלקוח</label>
          <Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} className="bg-stone-800 border-white/10 text-stone-100" data-testid="ff-edit-prod-contact" />
          <label className="block text-xs font-bold text-stone-300">טלפון איש הקשר</label>
          <Input value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} className="bg-stone-800 border-white/10 text-stone-100 text-left" dir="ltr" data-testid="ff-edit-prod-phone" />
          {/* Owner spec 2026-07-08f — production location/address. Used later in the WhatsApp schedule template. */}
          <label className="block text-xs font-bold text-stone-300">לוקיישן ההפקה (כתובת מלאה)</label>
          <Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="לדוגמה: רח' הרצל 12, תל אביב" className="bg-stone-800 border-white/10 text-stone-100" data-testid="ff-edit-prod-location" />
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg border border-white/15 bg-stone-800 hover:bg-stone-700 text-sm font-bold" data-testid="ff-edit-prod-cancel">ביטול</button>
            <button type="submit" className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-bold" data-testid="ff-edit-prod-save">שמור</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Add Dialog ----------------
function AddProductionDialog({ open, onOpenChange, onCreated, companyId, productions = [] }) {
  // Form state — note we no longer collect "income" (it's derived from
  // payment rows on the production page). We do collect:
  //   - client_name (שם החברה)
  //   - contact_name (שם איש הקשר)
  //   - client_budget (תקציב לקוח, numeric)
  // Files are STAGED locally and uploaded AFTER the production exists.
  const [form, setForm] = useState({
    name: "", client_name: "", contact_name: "",
    client_phone: "", client_email: "", client_whatsapp: "",
    client_budget: "", client_budget_includes_vat: false,
  });
  const [busy, setBusy] = useState(false);
  const [quoteFile, setQuoteFile] = useState(null);   // staged הצעת מחיר
  const [docFile, setDocFile] = useState(null);       // staged מסמך נוסף (additional expense receipt)
  // Previous-production picker — opens a dropdown listing every prior
  // production so the user can pick which client's contact info to copy.
  const [prevPickerOpen, setPrevPickerOpen] = useState(false);
  const quoteInputRef = useRef(null);
  const docInputRef = useRef(null);
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset everything when the dialog closes so opening fresh starts clean.
  useEffect(() => {
    if (!open) {
      setForm({ name: "", client_name: "", contact_name: "", client_phone: "", client_email: "", client_whatsapp: "", client_budget: "", client_budget_includes_vat: false });
      setQuoteFile(null);
      setDocFile(null);
    }
  }, [open]);

  // Pre-fill from a SELECTED previous production (excluding name +
  // budget — those should be project-specific). User picks from a
  // dropdown of every prior production.
  const fillFromProduction = (prev) => {
    if (!prev) return;
    setForm((f) => ({
      ...f,
      client_name: prev.client_name || "",
      contact_name: prev.contact_name || "",
      client_phone: prev.client_phone || "",
      client_email: prev.client_email || "",
      client_whatsapp: prev.client_whatsapp || "",
    }));
    setPrevPickerOpen(false);
    toast.success(`מולא מההפקה: ${prev.name}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("נדרש שם הפקה"); return; }
    setBusy(true);
    try {
      const prod = await createProduction({
        name: form.name.trim(),
        client_name: form.client_name.trim(),
        contact_name: form.contact_name.trim(),
        client_phone: form.client_phone.trim(),
        client_email: form.client_email.trim(),
        client_whatsapp: (form.client_whatsapp || form.client_phone).trim(),
        client_budget: Number(form.client_budget) || 0,
        client_budget_includes_vat: !!form.client_budget_includes_vat,
      }, companyId);
      // Upload staged files in the background — don't block the success popup.
      const uploads = [];
      if (quoteFile) uploads.push(uploadQuoteFile(prod.id, quoteFile, companyId).catch(() => null));
      if (docFile) uploads.push(uploadAdditionalExpense(prod.id, docFile, companyId, true).catch(() => null));
      if (uploads.length) await Promise.all(uploads);
      onCreated(prod);
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה ביצירה");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ff-add-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <DialogTitle className="text-right flex items-center gap-2">
                <span className={ffGradientText}>הפקה חדשה</span>
              </DialogTitle>
              <DialogDescription className="text-right text-stone-400">
                ניתן לעדכן את הפרטים בכל שלב.
              </DialogDescription>
            </div>
            {/* "השתמש בפרטים מהפקה קודמת" — dropdown picker (top-left) */}
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => setPrevPickerOpen((v) => !v)}
                disabled={!productions.length}
                className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-bold leading-tight bg-stone-800 hover:bg-stone-700 disabled:opacity-40 disabled:cursor-not-allowed border border-white/10 text-stone-100 rounded-xl px-3 py-2 transition"
                data-testid="ff-add-use-previous"
              >
                <History className="h-3.5 w-3.5 shrink-0" />
                <span className="text-right">השתמש בפרטים<br/>מהפקה קודמת</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${prevPickerOpen ? "rotate-180" : ""}`} />
              </button>

              {prevPickerOpen && productions.length > 0 && (
                <div
                  className="absolute end-0 mt-1 w-64 max-h-72 overflow-y-auto bg-stone-900 border border-white/10 rounded-xl shadow-2xl z-50"
                  data-testid="ff-add-prev-picker"
                >
                  <div className="sticky top-0 bg-stone-900 px-3 py-2 border-b border-white/5 text-[10px] uppercase tracking-widest text-stone-400 font-bold">
                    בחר הפקה להעתקת פרטי לקוח
                  </div>
                  <ul className="py-1">
                    {productions.map((prev) => (
                      <li key={prev.id}>
                        <button
                          type="button"
                          onClick={() => fillFromProduction(prev)}
                          className="w-full text-right px-3 py-2 hover:bg-stone-800 transition flex flex-col gap-0.5"
                          data-testid={`ff-add-prev-pick-${prev.id}`}
                        >
                          <span className="text-sm font-bold text-white truncate">{prev.name}</span>
                          {prev.client_name && (
                            <span className="text-[11px] text-stone-400 truncate">לקוח: {prev.client_name}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2" data-testid="ff-add-form">
          <FFField label="שם ההפקה *">
            <Input value={form.name} onChange={(e) => u("name", e.target.value)} placeholder="גבינה" autoFocus required data-testid="ff-add-name" className="bg-stone-800/60 border-emerald-500/40 text-white" />
          </FFField>
          <div className="grid grid-cols-2 gap-3">
            <FFField label="שם החברה">
              <Input value={form.client_name} onChange={(e) => u("client_name", e.target.value)} placeholder='חברת גבינה בע"מ' data-testid="ff-add-client" className="bg-stone-800/60 border-white/10 text-white" />
            </FFField>
            <FFField label="שם איש הקשר">
              <Input value={form.contact_name} onChange={(e) => u("contact_name", e.target.value)} placeholder="ערן כהן" data-testid="ff-add-contact-name" className="bg-stone-800/60 border-white/10 text-white" />
            </FFField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FFField label="טלפון">
              <Input dir="ltr" value={form.client_phone} onChange={(e) => u("client_phone", e.target.value)} placeholder="050-0000000" data-testid="ff-add-phone" className="bg-stone-800/60 border-white/10 text-white text-right" />
            </FFField>
            <FFField label="WhatsApp (לא חובה)">
              <Input dir="ltr" value={form.client_whatsapp} onChange={(e) => u("client_whatsapp", e.target.value)} placeholder="כברירת מחדל = טלפון" data-testid="ff-add-wa" className="bg-stone-800/60 border-white/10 text-white text-right" />
            </FFField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FFField label="תקציב פרויקט">
              <Input type="number" step="0.01" dir="ltr" value={form.client_budget} onChange={(e) => u("client_budget", e.target.value)} placeholder="50,000" data-testid="ff-add-budget" className="bg-stone-800/60 border-white/10 text-white text-right" />
              {/* פלוס מע"מ 18% — when ticked, the entered budget is
                  NET; we add 18% on top so the client pays gross. When
                  unticked, the client is עוסק פטור and no VAT applies
                  at all (rubric 3 in the summary is hidden). */}
              <label className="mt-1.5 flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.client_budget_includes_vat}
                  onChange={(e) => u("client_budget_includes_vat", e.target.checked)}
                  className="h-3.5 w-3.5 accent-fuchsia-500"
                  data-testid="ff-add-budget-vat"
                />
                <span className="text-[11px] text-stone-300">פלוס מע&quot;מ 18%</span>
              </label>
            </FFField>
            <FFField label="מסמכים">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => quoteInputRef.current?.click()}
                  className={`relative text-[11px] font-bold leading-tight rounded-lg px-2 py-2.5 border transition ${quoteFile ? "bg-emerald-900/60 border-emerald-500/60 text-emerald-100" : "bg-stone-800/60 border-white/10 hover:bg-stone-700 text-stone-100"}`}
                  data-testid="ff-add-attach-quote"
                  title={quoteFile ? quoteFile.name : ""}
                >
                  {quoteFile ? (
                    <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> הצעת מחיר ✓</span>
                  ) : (
                    <span className="inline-flex items-center gap-1"><Plus className="h-3 w-3" /> הוסף<br/>הצעת מחיר</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => docInputRef.current?.click()}
                  className={`relative text-[11px] font-bold leading-tight rounded-lg px-2 py-2.5 border transition ${docFile ? "bg-emerald-900/60 border-emerald-500/60 text-emerald-100" : "bg-stone-800/60 border-white/10 hover:bg-stone-700 text-stone-100"}`}
                  data-testid="ff-add-attach-doc"
                  title={docFile ? docFile.name : ""}
                >
                  {docFile ? (
                    <span className="inline-flex items-center gap-1"><Paperclip className="h-3 w-3" /> מסמך ✓</span>
                  ) : (
                    <span className="inline-flex items-center gap-1"><Plus className="h-3 w-3" /> הוסף<br/>מסמך</span>
                  )}
                </button>
                <input ref={quoteInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setQuoteFile(e.target.files?.[0] || null)} />
                <input ref={docInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
              </div>
              {(quoteFile || docFile) && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {quoteFile && (
                    <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-900/40 border border-emerald-500/30 text-emerald-200 rounded-full px-2 py-0.5 max-w-full">
                      <span className="truncate max-w-[120px]">הצעת מחיר: {quoteFile.name}</span>
                      <button type="button" onClick={() => setQuoteFile(null)} className="text-emerald-300 hover:text-emerald-100" data-testid="ff-add-remove-quote"><XIcon className="h-3 w-3" /></button>
                    </span>
                  )}
                  {docFile && (
                    <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-900/40 border border-emerald-500/30 text-emerald-200 rounded-full px-2 py-0.5 max-w-full">
                      <span className="truncate max-w-[120px]">מסמך: {docFile.name}</span>
                      <button type="button" onClick={() => setDocFile(null)} className="text-emerald-300 hover:text-emerald-100" data-testid="ff-add-remove-doc"><XIcon className="h-3 w-3" /></button>
                    </span>
                  )}
                </div>
              )}
            </FFField>
          </div>
          <FFField label="אימייל">
            <Input type="email" dir="ltr" value={form.client_email} onChange={(e) => u("client_email", e.target.value)} placeholder="contact@client.co.il" data-testid="ff-add-email" className="bg-stone-800/60 border-white/10 text-white text-right" />
          </FFField>
          <Button type="submit" disabled={busy} className={`${ffGradient} text-white font-bold w-full`} data-testid="ff-add-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "צור הפקה"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================
//   MULTI-PRODUCTION DIALOG — owner spec 2026-10-12
// =====================================================================
// Opens when the user clicks "הפקה מרובה". Creates one parent production
// (is_multi_production=true) and any number of sub-productions that each
// carry their own budget deducted from the parent's total.
function AddMultiProductionDialog({ open, onOpenChange, onMultiCreated, companyId }) {
  const emptyParent = {
    name: "", client_name: "", contact_name: "",
    client_phone: "", client_email: "", client_whatsapp: "",
    client_budget: "", client_budget_includes_vat: false,
  };
  const [parentForm, setParentForm] = useState(emptyParent);
  const [subProds, setSubProds] = useState([]);
  const [busy, setBusy] = useState(false);

  const totalBudget = Number(parentForm.client_budget) || 0;
  const allocatedBudget = subProds.reduce((s, p) => s + (Number(p.budget) || 0), 0);
  const remainingBudget = totalBudget - allocatedBudget;
  const isOverBudget = totalBudget > 0 && allocatedBudget > totalBudget;

  const up = (k, v) => setParentForm((f) => ({ ...f, [k]: v }));

  const addSubProd = () =>
    setSubProds((s) => [...s, { id: Math.random().toString(36).slice(2), name: "", budget: "" }]);

  const updateSub = (id, field, val) =>
    setSubProds((s) => s.map((p) => (p.id === id ? { ...p, [field]: val } : p)));

  const removeSub = (id) =>
    setSubProds((s) => s.filter((p) => p.id !== id));

  useEffect(() => {
    if (!open) {
      setParentForm(emptyParent);
      setSubProds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!parentForm.name.trim()) { toast.error("נדרש שם ההפקה המרובה"); return; }
    if (subProds.some((p) => !p.name.trim())) { toast.error("נדרש שם לכל תת הפקה"); return; }
    setBusy(true);
    try {
      // 1. Create parent production (is_multi_production flag)
      const parent = await createProduction({
        name: parentForm.name.trim(),
        client_name: parentForm.client_name.trim(),
        contact_name: parentForm.contact_name.trim(),
        client_phone: parentForm.client_phone.trim(),
        client_email: parentForm.client_email.trim(),
        client_whatsapp: (parentForm.client_whatsapp || parentForm.client_phone).trim(),
        client_budget: totalBudget,
        client_budget_includes_vat: !!parentForm.client_budget_includes_vat,
        is_multi_production: true,
      }, companyId);

      // 2. Create sub-productions (sequentially to keep stable order)
      const children = [];
      for (const sp of subProds) {
        const child = await createProduction({
          name: sp.name.trim(),
          client_name: parentForm.client_name.trim(),
          contact_name: parentForm.contact_name.trim(),
          client_phone: parentForm.client_phone.trim(),
          client_email: parentForm.client_email.trim(),
          client_whatsapp: (parentForm.client_whatsapp || parentForm.client_phone).trim(),
          client_budget: Number(sp.budget) || 0,
          parent_production_id: parent.id,
        }, companyId);
        children.push(child);
      }
      onMultiCreated(parent, children);
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה ביצירה");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        dir="rtl"
        className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="ff-add-multi-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <Layers className="h-5 w-5 text-pink-400" />
            <span className={ffGradientText}>הוסף הפקה מרובה</span>
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400">
            הגדר הפקה ראשית עם תת הפקות עצמאיות — כל תת הפקה מקבלת תקציב נפרד שיורד מהסכום הכולל.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 pt-2" data-testid="ff-add-multi-form">

          {/* ── Parent production block ────────────────────────────── */}
          <div className="space-y-3 p-4 rounded-xl bg-stone-800/50 border border-pink-500/20">
            <h3 className="text-sm font-bold text-pink-300 flex items-center gap-2">
              <Film className="h-4 w-4" />
              פרטי ההפקה הראשית
            </h3>

            <FFField label="שם ההפקה הכוללת *">
              <Input
                value={parentForm.name}
                onChange={(e) => up("name", e.target.value)}
                placeholder="קמפיין קיץ 2026"
                autoFocus
                required
                data-testid="ff-multi-parent-name"
                className="bg-stone-800/60 border-emerald-500/40 text-white"
              />
            </FFField>

            <div className="grid grid-cols-2 gap-3">
              <FFField label="שם החברה">
                <Input
                  value={parentForm.client_name}
                  onChange={(e) => up("client_name", e.target.value)}
                  placeholder='חברה בע"מ'
                  data-testid="ff-multi-client"
                  className="bg-stone-800/60 border-white/10 text-white"
                />
              </FFField>
              <FFField label="שם איש הקשר">
                <Input
                  value={parentForm.contact_name}
                  onChange={(e) => up("contact_name", e.target.value)}
                  placeholder="ישראל ישראלי"
                  data-testid="ff-multi-contact"
                  className="bg-stone-800/60 border-white/10 text-white"
                />
              </FFField>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FFField label="טלפון">
                <Input
                  dir="ltr"
                  value={parentForm.client_phone}
                  onChange={(e) => up("client_phone", e.target.value)}
                  placeholder="050-0000000"
                  data-testid="ff-multi-phone"
                  className="bg-stone-800/60 border-white/10 text-white text-right"
                />
              </FFField>
              <FFField label="WhatsApp (לא חובה)">
                <Input
                  dir="ltr"
                  value={parentForm.client_whatsapp}
                  onChange={(e) => up("client_whatsapp", e.target.value)}
                  placeholder="כברירת מחדל = טלפון"
                  data-testid="ff-multi-wa"
                  className="bg-stone-800/60 border-white/10 text-white text-right"
                />
              </FFField>
            </div>

            <FFField label="אימייל">
              <Input
                type="email"
                dir="ltr"
                value={parentForm.client_email}
                onChange={(e) => up("client_email", e.target.value)}
                placeholder="contact@client.co.il"
                data-testid="ff-multi-email"
                className="bg-stone-800/60 border-white/10 text-white text-right"
              />
            </FFField>

            <FFField label="תקציב כולל">
              <Input
                type="number"
                step="0.01"
                dir="ltr"
                value={parentForm.client_budget}
                onChange={(e) => up("client_budget", e.target.value)}
                placeholder="100,000"
                data-testid="ff-multi-total-budget"
                className="bg-stone-800/60 border-white/10 text-white text-right"
              />
              <label className="mt-1.5 flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!parentForm.client_budget_includes_vat}
                  onChange={(e) => up("client_budget_includes_vat", e.target.checked)}
                  className="h-3.5 w-3.5 accent-fuchsia-500"
                  data-testid="ff-multi-vat"
                />
                <span className="text-[11px] text-stone-300">פלוס מע&quot;מ 18%</span>
              </label>
            </FFField>
          </div>

          {/* ── Budget tracker ─────────────────────────────────────── */}
          {totalBudget > 0 && (
            <div
              className={`flex flex-wrap gap-4 p-3 rounded-xl border text-sm ${
                isOverBudget
                  ? "bg-red-950/40 border-red-500/40"
                  : "bg-stone-800/40 border-white/10"
              }`}
              data-testid="ff-multi-budget-tracker"
            >
              <span className="text-stone-400">
                תקציב: <strong className="text-white">{ils(totalBudget)}</strong>
              </span>
              <span className="text-stone-400">
                הוקצה: <strong className="text-amber-300">{ils(allocatedBudget)}</strong>
              </span>
              <span className={isOverBudget ? "text-red-400 font-bold" : "text-stone-400"}>
                נותר:{" "}
                <strong className={isOverBudget ? "text-red-400" : "text-emerald-400"}>
                  {ils(remainingBudget)}
                </strong>
              </span>
              {isOverBudget && (
                <span className="text-red-400 text-xs font-bold flex items-center gap-1 w-full">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  סכום תת ההפקות חורג מהתקציב הכולל
                </span>
              )}
            </div>
          )}

          {/* ── Sub-productions list ────────────────────────────────── */}
          {subProds.length > 0 && (
            <div className="space-y-3">
              {subProds.map((sp, idx) => (
                <div
                  key={sp.id}
                  className="p-4 rounded-xl bg-stone-800/60 border border-fuchsia-500/20"
                  data-testid={`ff-sub-prod-${idx}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-fuchsia-300 flex items-center gap-1.5">
                      <GitBranch className="h-3.5 w-3.5" />
                      תת הפקה {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSub(sp.id)}
                      className="text-stone-500 hover:text-red-400 transition"
                      data-testid={`ff-sub-remove-${idx}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FFField label="שם תת ההפקה *">
                      <Input
                        value={sp.name}
                        onChange={(e) => updateSub(sp.id, "name", e.target.value)}
                        placeholder={`פרק ${idx + 1}`}
                        required
                        data-testid={`ff-sub-name-${idx}`}
                        className="bg-stone-700/60 border-fuchsia-500/30 text-white"
                      />
                    </FFField>
                    <FFField label="תקציב תת הפקה">
                      <Input
                        type="number"
                        step="0.01"
                        dir="ltr"
                        value={sp.budget}
                        onChange={(e) => updateSub(sp.id, "budget", e.target.value)}
                        placeholder="25,000"
                        data-testid={`ff-sub-budget-${idx}`}
                        className="bg-stone-700/60 border-fuchsia-500/30 text-white text-right"
                      />
                      {totalBudget > 0 && Number(sp.budget) > 0 && (
                        <span className="text-[10px] text-stone-400 mt-0.5 block">
                          {Math.round((Number(sp.budget) / totalBudget) * 100)}% מהתקציב הכולל
                        </span>
                      )}
                    </FFField>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── "הוסף תת הפקה" prominent button ────────────────────── */}
          <button
            type="button"
            onClick={addSubProd}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border-2 border-dashed border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-950/30 hover:border-fuchsia-400 hover:text-fuchsia-200 transition-all font-bold text-sm"
            data-testid="ff-multi-add-sub-btn"
          >
            <Plus className="h-5 w-5" />
            הוסף תת הפקה
          </button>

          {/* ── Submit ─────────────────────────────────────────────── */}
          <Button
            type="submit"
            disabled={busy}
            className={`${ffGradient} text-white font-bold w-full`}
            data-testid="ff-multi-submit"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : subProds.length > 0 ? (
              `צור הפקה מרובה (${1 + subProds.length} הפקות)`
            ) : (
              "צור הפקה מרובה"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FFField({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-stone-300 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

// =====================================================================
//   INLINE PRODUCTION FORM — exact clone of AddProductionDialog as an
//   inline card (no Dialog shell). Same fields, same design, same logic.
// =====================================================================
function InlineProductionForm({ type, companyId, productions, onCreated, onClose }) {
  const [form, setForm] = useState({
    name: "", client_name: "", contact_name: "",
    client_phone: "", client_email: "", client_whatsapp: "",
    client_budget: "", client_budget_includes_vat: false,
  });
  const [busy, setBusy] = useState(false);
  const [quoteFile, setQuoteFile] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [prevPickerOpen, setPrevPickerOpen] = useState(false);
  const quoteInputRef = useRef(null);
  const docInputRef = useRef(null);
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fillFromProduction = (prev) => {
    if (!prev) return;
    setForm((f) => ({
      ...f,
      client_name: prev.client_name || "",
      contact_name: prev.contact_name || "",
      client_phone: prev.client_phone || "",
      client_email: prev.client_email || "",
      client_whatsapp: prev.client_whatsapp || "",
    }));
    setPrevPickerOpen(false);
    toast.success(`מולא מההפקה: ${prev.name}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("נדרש שם הפקה"); return; }
    setBusy(true);
    try {
      if (type === "single") {
        const prod = await createProduction({
          name: form.name.trim(),
          client_name: form.client_name.trim(),
          contact_name: form.contact_name.trim(),
          client_phone: form.client_phone.trim(),
          client_email: form.client_email.trim(),
          client_whatsapp: (form.client_whatsapp || form.client_phone).trim(),
          client_budget: Number(form.client_budget) || 0,
          client_budget_includes_vat: !!form.client_budget_includes_vat,
        }, companyId);
        const uploads = [];
        if (quoteFile) uploads.push(uploadQuoteFile(prod.id, quoteFile, companyId).catch(() => null));
        if (docFile) uploads.push(uploadAdditionalExpense(prod.id, docFile, companyId, true).catch(() => null));
        if (uploads.length) await Promise.all(uploads);
        onCreated(prod);
      } else {
        const prod = await createProduction({
          name: form.name.trim(),
          client_name: form.client_name.trim(),
          contact_name: form.contact_name.trim(),
          client_phone: form.client_phone.trim(),
          client_email: form.client_email.trim(),
          client_whatsapp: (form.client_whatsapp || form.client_phone).trim(),
          client_budget: Number(form.client_budget) || 0,
          client_budget_includes_vat: !!form.client_budget_includes_vat,
          is_multi_production: true,
        }, companyId);
        onCreated(prod);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה ביצירה");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-2xl border border-white/10 bg-stone-900 text-stone-100 shadow-2xl overflow-hidden"
      data-testid="ff-inline-create-form"
    >
      {/* Gradient top-edge accent */}
      <div className={`h-[2px] w-full ${ffGradient}`} />

      <div className="p-5 sm:p-6">
        {/* ── Header row ── */}
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h2 className={`font-heading font-extrabold text-xl leading-tight ${ffGradientText}`}>
              הפקה חדשה
            </h2>
            <p className="text-xs text-stone-400 mt-1">
              {type === "single" ? "ניתן לעדכן את הפרטים בכל שלב." : "הפקה מרובה — תת הפקות יתווספו מהדף הפנימי."}
            </p>
          </div>

          {/* Right-side controls */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Previous-productions picker (single only) */}
            {type === "single" && productions && productions.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPrevPickerOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-[10px] font-bold leading-tight bg-stone-800 hover:bg-stone-700 border border-white/10 text-stone-100 rounded-xl px-3 py-2 transition"
                  data-testid="ff-inline-use-previous"
                >
                  <History className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-right">הפקות<br/>קודמות</span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${prevPickerOpen ? "rotate-180" : ""}`} />
                </button>
                {prevPickerOpen && (
                  <div className="absolute end-0 mt-1 w-64 max-h-72 overflow-y-auto bg-stone-900 border border-white/10 rounded-xl shadow-2xl z-50" data-testid="ff-inline-prev-picker">
                    <div className="sticky top-0 bg-stone-900 px-3 py-2 border-b border-white/5 text-[10px] uppercase tracking-widest text-stone-400 font-bold">
                      בחר הפקה להעתקת פרטי לקוח
                    </div>
                    <ul className="py-1">
                      {productions.map((prev) => (
                        <li key={prev.id}>
                          <button type="button" onClick={() => fillFromProduction(prev)} className="w-full text-right px-3 py-2 hover:bg-stone-800 transition flex flex-col gap-0.5" data-testid={`ff-inline-prev-pick-${prev.id}`}>
                            <span className="text-sm font-bold text-white truncate">{prev.name}</span>
                            {prev.client_name && <span className="text-[11px] text-stone-400 truncate">לקוח: {prev.client_name}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {/* Close X */}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-stone-500 hover:text-stone-200 hover:bg-white/5 transition"
              data-testid="ff-inline-create-close"
              aria-label="סגור"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* ── Form ── */}
        <form onSubmit={submit} className="space-y-4" data-testid="ff-inline-form">
          {/* Production name */}
          <FFField label={<>שם ההפקה <span className="text-pink-400">*</span></>}>
            <Input
              value={form.name}
              onChange={(e) => u("name", e.target.value)}
              placeholder={type === "single" ? "גבינה" : "קמפיין קיץ 2026"}
              autoFocus
              required
              className="bg-stone-800/60 border-emerald-500/40 text-white"
              data-testid="ff-inline-name"
            />
          </FFField>

          {/* Company + contact */}
          <div className="grid grid-cols-2 gap-3">
            <FFField label="שם החברה">
              <Input value={form.client_name} onChange={(e) => u("client_name", e.target.value)} placeholder='חברת גבינה בע"מ' className="bg-stone-800/60 border-white/10 text-white" data-testid="ff-inline-client" />
            </FFField>
            <FFField label="שם איש הקשר">
              <Input value={form.contact_name} onChange={(e) => u("contact_name", e.target.value)} placeholder="ערן כהן" className="bg-stone-800/60 border-white/10 text-white" data-testid="ff-inline-contact" />
            </FFField>
          </div>

          {/* Phone + WhatsApp */}
          <div className="grid grid-cols-2 gap-3">
            <FFField label="טלפון">
              <Input dir="ltr" value={form.client_phone} onChange={(e) => u("client_phone", e.target.value)} placeholder="050-0000000" className="bg-stone-800/60 border-white/10 text-white text-right" data-testid="ff-inline-phone" />
            </FFField>
            <FFField label="WhatsApp (לא חובה)">
              <Input dir="ltr" value={form.client_whatsapp} onChange={(e) => u("client_whatsapp", e.target.value)} placeholder="כברירת מחדל = טלפון" className="bg-stone-800/60 border-white/10 text-white text-right" data-testid="ff-inline-wa" />
            </FFField>
          </div>

          {/* Budget + docs */}
          <div className="grid grid-cols-2 gap-3">
            <FFField label={type === "multi" ? "תקציב כולל" : "תקציב פרויקט"}>
              <Input
                type="number" step="0.01" dir="ltr"
                value={form.client_budget}
                onChange={(e) => u("client_budget", e.target.value)}
                placeholder="50,000"
                className="bg-stone-800/60 border-white/10 text-white text-right"
                data-testid="ff-inline-budget"
              />
              <label className="mt-1.5 flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.client_budget_includes_vat}
                  onChange={(e) => u("client_budget_includes_vat", e.target.checked)}
                  className="h-3.5 w-3.5 accent-fuchsia-500"
                  data-testid="ff-inline-vat"
                />
                <span className="text-[11px] text-stone-300">פלוס מע&quot;מ 18%</span>
              </label>
            </FFField>
            <FFField label="מסמכים">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => quoteInputRef.current?.click()}
                  className={`text-[11px] font-bold leading-tight rounded-lg px-2 py-2.5 border transition ${quoteFile ? "bg-emerald-900/60 border-emerald-500/60 text-emerald-100" : "bg-stone-800/60 border-white/10 hover:bg-stone-700 text-stone-100"}`}
                  data-testid="ff-inline-attach-quote"
                >
                  {quoteFile
                    ? <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> הצעת מחיר ✓</span>
                    : <span className="inline-flex items-center gap-1"><Plus className="h-3 w-3" /> הוסף<br/>הצעת מחיר</span>}
                </button>
                <button
                  type="button"
                  onClick={() => docInputRef.current?.click()}
                  className={`text-[11px] font-bold leading-tight rounded-lg px-2 py-2.5 border transition ${docFile ? "bg-emerald-900/60 border-emerald-500/60 text-emerald-100" : "bg-stone-800/60 border-white/10 hover:bg-stone-700 text-stone-100"}`}
                  data-testid="ff-inline-attach-doc"
                >
                  {docFile
                    ? <span className="inline-flex items-center gap-1"><Paperclip className="h-3 w-3" /> מסמך ✓</span>
                    : <span className="inline-flex items-center gap-1"><Plus className="h-3 w-3" /> הוסף<br/>מסמך</span>}
                </button>
                <input ref={quoteInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setQuoteFile(e.target.files?.[0] || null)} />
                <input ref={docInputRef}   type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
              </div>
            </FFField>
          </div>

          {/* Email */}
          <FFField label="אימייל">
            <Input type="email" dir="ltr" value={form.client_email} onChange={(e) => u("client_email", e.target.value)} placeholder="contact@client.co.il" className="bg-stone-800/60 border-white/10 text-white text-right" data-testid="ff-inline-email" />
          </FFField>

          {/* Submit */}
          <Button
            type="submit"
            disabled={busy}
            className={`${ffGradient} text-white font-bold w-full`}
            data-testid="ff-inline-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "צור הפקה"}
          </Button>
        </form>
      </div>
    </div>
  );
}

// =====================================================================
//        PARTNER PROFIT CARDS — Daniel Hassin & Nir Dadon
// =====================================================================
//
// Owner spec 2026-06-14:
//   • Each partner has a dedicated card on the dashboard summing up:
//       1. Staff earnings  — total they were paid via crew rows
//          across ALL productions in the selected period.
//       2. Profit share    — their cut of the agency's NET profit
//          after tax (50% each per the partnership agreement).
//       3. Grand total     — staff_earnings + profit_share.
//   • Period (month/year) follows the same picker as the income cubes.
//   • Hidden when both partners have zero (e.g. empty company / no data).
function PartnerProfitCards({ partners, periodLabel, companyId }) {
  // Owner spec 2026-08-09 — always render all three tiles (ניר, דניאל,
  // Full·Frame). Empty tiles read as "עדיין לא שובץ", which is meaningful
  // and matches the manual-split editor on each production page.
  const list = partners || [];
  const [open, setOpen] = useState(false);
  const [detailsFor, setDetailsFor] = useState(null);  // {key, name, items, total_net, count}
  const [loadingDetails, setLoadingDetails] = useState(false);
  if (!list || list.length === 0) return null;

  const openDetails = async (p) => {
    setLoadingDetails(true);
    try {
      const data = await getPartnerDetails(p.key, companyId);
      setDetailsFor(data);
    } catch (e) { toast.error(e.response?.data?.detail || "טעינת פירוט נכשלה"); }
    finally { setLoadingDetails(false); }
  };

  // Sky/violet vs amber/gold gradients give a clear visual distinction
  // between the partners without making any look secondary.
  const palette = [
    {
      bg: "bg-gradient-to-br from-violet-950 via-fuchsia-950/70 to-stone-900",
      border: "border-violet-500/40",
      iconBg: "bg-violet-500/15 text-violet-300",
      label: "text-violet-200",
      big: "text-violet-100",
      detailLabel: "text-violet-200/70",
      crown: "text-violet-300",
    },
    {
      bg: "bg-gradient-to-br from-amber-950 via-orange-950/70 to-stone-900",
      border: "border-amber-500/40",
      iconBg: "bg-amber-500/15 text-amber-300",
      label: "text-amber-200",
      big: "text-amber-100",
      detailLabel: "text-amber-200/70",
      crown: "text-amber-300",
    },
    {
      bg: "bg-gradient-to-br from-sky-950 via-cyan-950/70 to-stone-900",
      border: "border-sky-500/40",
      iconBg: "bg-sky-500/15 text-sky-300",
      label: "text-sky-200",
      big: "text-sky-100",
      detailLabel: "text-sky-200/70",
      crown: "text-sky-300",
    },
  ];

  return (
    <section className="space-y-2" data-testid="ff-partner-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-stone-700 hover:bg-stone-700/80 transition px-4 py-3"
        data-testid="ff-partner-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <Crown className="h-4 w-4 text-amber-400" />
          <span className="font-heading text-sm sm:text-base font-extrabold text-white">רווחי שותפים</span>
          <span className="text-[10px] text-stone-400 font-bold">· {periodLabel}</span>
        </div>
        <ChevronDown className={`h-5 w-5 text-amber-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="ff-partner-grid">
        {list.map((p, i) => {
          const c = palette[i % palette.length];
          const isAgency = !p.phone;
          return (
            <article
              key={p.key || p.name}
              className={`rounded-xl border ${c.border} ${c.bg} p-2.5 shadow-md shadow-black/30`}
              data-testid={`ff-partner-card-${p.key || i}`}
            >
              <header className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className={`h-7 w-7 rounded-lg ${c.iconBg} flex items-center justify-center`}>
                    <Users className="h-3.5 w-3.5" />
                  </div>
                  <div>
                    <h3 className={`font-heading text-xs sm:text-sm font-extrabold ${c.label} leading-tight`}>{p.name}</h3>
                    <p className="text-[9px] text-stone-400 font-semibold leading-none">{isAgency ? "חברה" : "שותף"}</p>
                  </div>
                </div>
                <Crown className={`h-3.5 w-3.5 ${c.crown}`} />
              </header>
              <div className={`font-heading font-extrabold tabular-nums leading-none text-lg sm:text-xl ${c.big} mb-1.5`} data-testid={`ff-partner-total-${p.key || i}`}>
                {ils(p.total)}
              </div>
              <ul className="space-y-0.5">
                {!isAgency && (
                  <li className="flex items-center justify-between gap-2">
                    <span className={`text-[10px] ${c.detailLabel} font-semibold`}>שכר נטו (ספק)</span>
                    <span className="text-[11px] font-bold tabular-nums text-white" data-testid={`ff-partner-staff-${p.key || i}`}>
                      {ils(p.staff_earnings)}
                    </span>
                  </li>
                )}
                <li className="flex items-center justify-between gap-2">
                  <span className={`text-[10px] ${c.detailLabel} font-semibold`}>{isAgency ? "חסכון חברה" : "חלק ברווח"}</span>
                  <span className="text-[11px] font-bold tabular-nums text-white" data-testid={`ff-partner-share-${p.key || i}`}>
                    {ils(p.profit_share)}
                  </span>
                </li>
              </ul>
              {/* "פירוט הפקות" — opens a modal listing every crew row
                  matched to this partner across all productions, with
                  the production name, role and NET amount. */}
              {!isAgency && (
                <button
                  type="button"
                  onClick={() => openDetails(p)}
                  disabled={loadingDetails}
                  className={`mt-2 w-full inline-flex items-center justify-center gap-1 rounded-lg border ${c.border} bg-black/30 hover:bg-black/45 text-[11px] font-bold ${c.label} py-1.5 transition disabled:opacity-50`}
                  data-testid={`ff-partner-details-btn-${p.key || i}`}
                >
                  {loadingDetails ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListOrdered className="h-3.5 w-3.5" />}
                  פירוט הפקות
                </button>
              )}
            </article>
          );
        })}
      </div>
      )}

      {/* Partner production-breakdown modal — owner spec 2026-06-15h. */}
      <PartnerDetailsDialog
        data={detailsFor}
        onClose={() => setDetailsFor(null)}
      />
    </section>
  );
}

// ---- Partner Details dialog — lists every crew row matched to the partner
// across all productions. Sum at the bottom matches partner.staff_earnings.
function PartnerDetailsDialog({ data, onClose }) {
  const open = !!data;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        dir="rtl"
        className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="ff-partner-details-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <ListOrdered className="h-4 w-4 text-amber-300" />
            <span className={ffGradientText}>פירוט הפקות{data?.name ? ` · ${data.name}` : ""}</span>
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400">
            רשימת כל השורות המשויכות לשותף (לפי טלפון) — סכום נטו לפני מע״מ.
          </DialogDescription>
        </DialogHeader>
        {data?.items?.length ? (
          <div className="space-y-2 pt-1" data-testid="ff-partner-details-list">
            <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest text-stone-400 font-bold border-b border-white/10 pb-1.5">
              <div className="col-span-6">הפקה</div>
              <div className="col-span-3">תפקיד</div>
              <div className="col-span-3 text-left">סכום נטו</div>
            </div>
            {data.items.map((it, idx) => (
              <div
                key={`${it.production_id}-${it.row_id}-${idx}`}
                className="grid grid-cols-12 gap-2 items-center text-sm bg-stone-800/60 border border-white/5 rounded-lg px-3 py-2"
                data-testid={`ff-partner-details-row-${idx}`}
              >
                <div className="col-span-6 truncate font-bold text-white">{it.production_name}</div>
                <div className="col-span-3 truncate text-stone-300">{it.role || "—"}</div>
                <div className="col-span-3 text-left font-extrabold tabular-nums text-emerald-200">
                  {ils(it.amount_net)}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-white/10 mt-2">
              <span className="text-xs text-stone-400 font-semibold">{data.count} שורות</span>
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">סה״כ נטו</span>
                <span className="text-lg font-extrabold tabular-nums text-amber-200" data-testid="ff-partner-details-total">
                  {ils(data.total_net)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <p className="py-8 text-center text-stone-500 text-sm" data-testid="ff-partner-details-empty">
            עוד אין שורות צוות המשויכות לשותף זה.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
